import { useMemo, useState } from 'react'
import { Alert, Button, Card, Flex, Input, Select, Space, Table, Tag, Tooltip, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { PlusOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { Can } from '@/shared/auth/Can'
import { formatDateTime, utcTooltip } from '@/shared/lib/format'
import {
  applyLocalList,
  byInstant,
  byNumber,
  byText,
  localSortChange,
  sortColumn,
  useLocalListState,
} from '@/shared/lib/localList'
import { EmptyState } from '@/shared/ui/EmptyState'
import { PageHeader } from '@/shared/ui/PageHeader'
import { RowActions } from '@/shared/ui/table/RowActions'
import { RatingCriterionFormModal } from '@/features/hr/RatingCriterionFormModal'
import {
  fetchRatingCriteria,
  setRatingCriterionActive,
  type RatingCriterion,
} from '@/features/hr/ratingApi'

/** Server sahifalamaydi (`activeOnly` yagona parametr) — butun katalog keladi, qidiruv va saralash mijozda. */
export function HrRatingCriteriaPage() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<RatingCriterion | null>(null)

  const list = useLocalListState({ defaultSort: { field: 'createdAt', order: 'descend' } })
  const activeFilter = list.filter('active') ?? 'active'
  const activeOnly = activeFilter === 'active'

  const criteria = useQuery({
    queryKey: queryKeys.hr.ratingCriteria(activeOnly),
    queryFn: () => fetchRatingCriteria(activeOnly),
  })

  const toggle = useMutation({
    mutationFn: (row: RatingCriterion) => setRatingCriterionActive(row.id, !row.active),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.hr.all }),
  })

  const rows = useMemo(() => criteria.data ?? [], [criteria.data])

  const visible = useMemo(
    () =>
      applyLocalList(rows, {
        search: list.search,
        searchFields: (row) => [row.name, row.description ?? ''],
        filters: {
          active: (row, value) => (value === 'inactive' ? !row.active : true),
        },
        filterState: list.state.filters,
        comparators: {
          name: byText((row) => row.name),
          sortOrder: byNumber((row) => row.sortOrder),
          ratingCount: byNumber((row) => row.ratingCount),
          createdAt: byInstant((row) => row.createdAt),
        },
        sort: list.sort,
      }),
    [rows, list.search, list.state.filters, list.sort],
  )

  const columns: ColumnsType<RatingCriterion> = [
    {
      key: '__rowNo',
      title: t('dataTable.rowNo'),
      width: 56,
      align: 'center',
      render: (_v, _r, index) => <Typography.Text type="secondary">{index + 1}</Typography.Text>,
    },
    {
      key: 'name',
      title: t('hr.ratingCriterion.name'),
      ...sortColumn(list.sort, 'name'),
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <Typography.Text strong={row.active} type={row.active ? undefined : 'secondary'}>
            {row.name}
          </Typography.Text>
          {row.description === null ? null : (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {row.description}
            </Typography.Text>
          )}
        </Space>
      ),
    },
    {
      key: 'sortOrder',
      title: t('hr.ratingCriterion.sortOrder'),
      width: 120,
      align: 'right',
      ...sortColumn(list.sort, 'sortOrder'),
      render: (_, row) => row.sortOrder,
    },
    {
      key: 'active',
      title: t('hr.ratingCriterion.state'),
      width: 130,
      render: (_, row) =>
        row.active ? (
          <Tag color="green">{t('hr.ratingCriterion.stateActive')}</Tag>
        ) : (
          <Tag>{t('hr.ratingCriterion.stateInactive')}</Tag>
        ),
    },
    {
      key: 'ratingCount',
      title: t('hr.ratingCriterion.ratingCount'),
      width: 160,
      ...sortColumn(list.sort, 'ratingCount'),
      render: (_, row) => t('hr.rating.ratingCount', { count: row.ratingCount }),
    },
    {
      key: 'createdAt',
      title: t('common.createdAt'),
      width: 180,
      ...sortColumn(list.sort, 'createdAt'),
      render: (_, row) =>
        row.createdAt === null ? (
          <Typography.Text type="secondary">{t('common.dash')}</Typography.Text>
        ) : (
          <Tooltip title={utcTooltip(row.createdAt)}>{formatDateTime(row.createdAt)}</Tooltip>
        ),
    },
    {
      key: 'actions',
      title: '',
      width: 56,
      align: 'center',
      fixed: 'right',
      render: (_, row) => (
        <RowActions
          disabled={toggle.isPending}
          items={[
            {
              key: 'edit',
              label: t('common.edit'),
              onClick: () => {
                setEditing(row)
                setFormOpen(true)
              },
              permission: 'hr.ratingcriterion.write',
            },
            {
              key: 'toggle',
              label: row.active
                ? t('hr.ratingCriterion.deactivate')
                : t('hr.ratingCriterion.activate'),
              onClick: () => toggle.mutate(row),
              permission: 'hr.ratingcriterion.write',
              danger: row.active,
              divider: true,
            },
          ]}
        />
      ),
    },
  ]

  return (
    <>
      <PageHeader
        title={t('nav.hrRatingCriteria')}
        description={t('pageIntro.hrRatingCriteria')}
        extra={
          <Can permission="hr.ratingcriterion.write">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditing(null)
                setFormOpen(true)
              }}
            >
              {t('hr.ratingCriterion.createTitle')}
            </Button>
          </Can>
        }
      />

      {criteria.isError ? (
        <Alert type="error" showIcon style={{ marginBottom: 16 }} message={translateError(criteria.error, t)} />
      ) : null}
      {toggle.isError ? (
        <Alert
          type="error"
          showIcon
          closable
          onClose={() => toggle.reset()}
          style={{ marginBottom: 16 }}
          message={translateError(toggle.error, t)}
        />
      ) : null}

      <Card title={t('hr.ratingCriterion.listTitle', { count: rows.length })}>
        <Alert type="info" showIcon style={{ marginBottom: 16 }} message={t('hr.ratingCriterion.noDeleteNote')} />

        <Flex wrap gap="small" align="center" style={{ marginBottom: 16 }}>
          <Input.Search
            allowClear
            value={list.searchText}
            onChange={(e) => list.setSearchText(e.target.value)}
            placeholder={t('hr.ratingCriterion.searchPlaceholder')}
            style={{ width: 260, maxWidth: '100%' }}
          />
          <Select<string>
            value={activeFilter}
            onChange={(value) => list.setFilter('active', value)}
            aria-label={t('listView.statusFilter')}
            style={{ width: 170 }}
            options={[
              { value: 'active', label: t('listView.active') },
              { value: 'inactive', label: t('listView.inactive') },
            ]}
          />
          {list.narrowed ? (
            <>
              <Typography.Text type="secondary">
                {t('listView.shown', { shown: visible.length, total: rows.length })}
              </Typography.Text>
              <Button type="link" size="small" onClick={list.reset}>
                {t('dataTable.clearFilters')}
              </Button>
            </>
          ) : null}
        </Flex>

        <Table<RatingCriterion>
          rowKey={(row) => row.id}
          columns={columns}
          dataSource={[...visible]}
          loading={criteria.isPending}
          pagination={false}
          size="small"
          scroll={{ x: 900 }}
          onChange={localSortChange<RatingCriterion>(list.patch)}
          locale={{
            emptyText:
              rows.length > 0 && list.narrowed ? (
                <EmptyState
                  variant="no-data"
                  title={t('listView.noMatch')}
                  description={t('listView.noMatchHint', { total: rows.length })}
                  actionLabel={t('dataTable.clearFilters')}
                  onAction={list.reset}
                />
              ) : activeOnly ? (
                <EmptyState
                  variant="no-data"
                  title={t('listView.activeOnlyEmpty')}
                  description={t('listView.activeOnlyEmptyHint')}
                  actionLabel={t('listView.activeOnlyEmptyAction')}
                  onAction={() => list.setFilter('active', 'inactive')}
                />
              ) : (
                <EmptyState
                  variant="no-data"
                  title={t('hr.ratingCriterion.empty')}
                  description={t('hr.ratingCriterion.emptyHint')}
                />
              ),
          }}
        />

        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {t('listView.fullListNote')}
        </Typography.Text>
      </Card>

      <RatingCriterionFormModal
        open={formOpen}
        criterion={editing}
        onClose={() => setFormOpen(false)}
      />
    </>
  )
}
